import { AppDispatch } from '.';
import { authActions } from './auth';
import { userActions } from './user';
import { uiActions } from './ui';
import { messagesActions } from './messages';
import { API_ENDPOINTS } from '../config/api';
import { socketService } from '../services/socketService';
import { resetAppBadge } from '../utils/appBadge';

/**
 * Ask the server to deliver a sign-in credential — a magic link for a
 * never-verified email, or a 6-digit code for a returning user. The
 * response tells us which mode the server chose so the UI can render the
 * matching sub-flow without a second round trip.
 */
export const requestLoginToken = (email: string, turnstileToken: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      const response = await fetch(API_ENDPOINTS.auth.requestToken, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, turnstileToken }),
      });

      if (!response.ok) {
        const error = await response.json();
        // `no_account` is a signal — not an error to shout about via toast.
        // The SignInForm renders an inline "Sign up" CTA instead.
        if (error.code === 'no_account') {
          return { success: false, code: 'no_account', error: error.message };
        }
        throw new Error(error.message || 'Failed to request login token');
      }

      const data = await response.json();
      const mode: 'link' | 'code' = data.mode === 'code' ? 'code' : 'link';

      // In development, the token is returned in the response
      // In production, it would only be sent via email
      if (data.token) {
        console.log('='.repeat(60));
        console.log('🔐 LOGIN TOKEN (Development Only)');
        console.log('='.repeat(60));
        console.log(`Email: ${email}`);
        console.log(`Token: ${data.token}`);
        console.log(`\nLogin URL: http://localhost:3001/login?token=${data.token}&email=${encodeURIComponent(email)}`);
        console.log('='.repeat(60));
      }

      dispatch(
        uiActions.addNotification({
          message: data.message || (mode === 'code'
            ? 'Sign-in code sent to your email!'
            : 'Login link sent to your email!'),
        })
      );

      return { success: true, mode, token: data.token };
    } catch (error: any) {
      dispatch(
        uiActions.addNotification({
          message: error.message || 'Failed to request login token',
        })
      );
      return { success: false, error: error.message };
    }
  };
};

/**
 * Exchange a 6-digit sign-in code for an authenticated session. Mirrors
 * verifyLoginToken's success handling — same Redux dispatch, same socket
 * connect — so downstream code doesn't need to care which path signed the
 * user in.
 */
export const verifyLoginCode = (email: string, code: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      const response = await fetch(API_ENDPOINTS.auth.verifyCode, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, code }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Invalid or expired code');
      }

      const data = await response.json();

      dispatch(
        authActions.setAuthenticated({
          email: data.user.email,
          token: '',
          jwt: data.jwt,
          userId: data.user.id,
          isAdmin: data.user.is_admin === true,
          role: data.user.role ?? 'user',
        })
      );

      dispatch(uiActions.addNotification({ message: 'Successfully logged in!' }));
      socketService.connect(data.jwt);
      return { success: true, user: data.user };
    } catch (error: any) {
      dispatch(uiActions.addNotification({ message: error.message || 'Failed to verify code' }));
      return { success: false, error: error.message };
    }
  };
};

/**
 * Verify a login token and authenticate the user
 */
export const verifyLoginToken = (email: string, token: string, turnstileToken: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      const response = await fetch(API_ENDPOINTS.auth.verifyToken, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // let the browser store the httpOnly session cookie
        body: JSON.stringify({ email, token, turnstileToken }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Invalid or expired token');
      }

      const data = await response.json();

      dispatch(
        authActions.setAuthenticated({
          email: data.user.email,
          token: token,
          jwt: data.jwt,
          userId: data.user.id,
          isAdmin: data.user.is_admin === true,
          role: data.user.role ?? 'user',
        })
      );

      dispatch(
        uiActions.addNotification({
          message: 'Successfully logged in!',
        })
      );

      // Connect to Socket.io for real-time messaging
      socketService.connect(data.jwt);

      return { success: true, user: data.user };
    } catch (error: any) {
      dispatch(
        uiActions.addNotification({
          message: error.message || 'Failed to verify token',
        })
      );
      return { success: false, error: error.message };
    }
  };
};

/**
 * Logout the current user
 */
export const logout = () => {
  return async (dispatch: AppDispatch) => {
    // Disconnect from Socket.io
    socketService.disconnect();

    // Clear messages state
    dispatch(messagesActions.clearMessages());

    // Clear the app-icon badge on installed PWAs
    resetAppBadge();

    // Best-effort: clear the httpOnly session cookie on the server.
    try {
      await fetch(API_ENDPOINTS.auth.logout, { method: 'POST', credentials: 'include' });
    } catch (_) { /* offline is fine — client state is authoritative for the tab */ }

    dispatch(authActions.logout());
    dispatch(userActions.clearUser());
    dispatch(
      uiActions.addNotification({
        message: 'Logged out successfully',
      })
    );
  };
};

/**
 * Hydrate session from the httpOnly cookie.
 * Called on app boot when localStorage is empty (Safari ITP wiped it) or on
 * every boot as a background refresh. If the cookie is valid, /me returns the
 * user + a fresh JWT and we re-populate Redux + localStorage.
 */
export const hydrateSession = () => {
  return async (dispatch: AppDispatch) => {
    try {
      const response = await fetch(API_ENDPOINTS.auth.me, { credentials: 'include' });
      if (!response.ok) return { success: false };

      const data = await response.json();
      if (!data?.user || !data?.jwt) return { success: false };

      dispatch(
        authActions.setAuthenticated({
          email: data.user.email,
          token: '',
          jwt: data.jwt,
          userId: data.user.id,
          isAdmin: data.user.is_admin === true,
          role: data.user.role ?? 'user',
        })
      );
      socketService.connect(data.jwt);
      return { success: true, user: data.user };
    } catch (_) {
      return { success: false };
    }
  };
};
